import { apiClient } from './apiClient';

export interface Vehicle {
  id: number;
  model: string;
  license_plate: string;
  vehicle_number?: string;
  department?: string;
  current_mileage?: number;
  registration_date?: string;
  status: string;
  image_url?: string;
  created_at?: string;
  updated_at?: string;
}

export interface VehicleSummary {
  total_vehicles: number;
  available_vehicles: number;
  in_use_vehicles: number;
  maintenance_vehicles: number;
  total_maintenance_cost: number;
  total_fuel_cost: number;
  total_operational_cost: number;
  pending_maintenance_count: number;
  expiring_documents_count: number;
  active_alerts_count: number;
}

export interface VehicleRequestData {
  vehicle_id: number;
  departure_mileage: number;
  purpose: string;
  destination: string;
}

export const vehicleService = {
  async getVehicles(skip: number = 0, limit: number = 100): Promise<Vehicle[]> {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });
    
    return apiClient.get(`/vehicles/?${params.toString()}`);
  },

  async getVehicleSummary(): Promise<VehicleSummary> {
    return apiClient.get('/vehicle-management/summary');
  },

  async createVehicleRequest(requestData: VehicleRequestData): Promise<any> {
    return apiClient.post('/vehicles/requests/', requestData);
  },

  async createVehicle(vehicleData: any, imageFile?: File): Promise<Vehicle> {
    const formData = new FormData();
    
    // Add only the exact fields that the backend endpoint expects
    formData.append('model', vehicleData.model);
    formData.append('license_plate', vehicleData.license_plate);
    
    // Optional fields - only append if they have values
    if (vehicleData.vehicle_number) {
      formData.append('vehicle_number', vehicleData.vehicle_number);
    }
    if (vehicleData.current_mileage) {
      formData.append('current_mileage', vehicleData.current_mileage.toString());
    }
    if (vehicleData.registration_date) {
      formData.append('registration_date', vehicleData.registration_date);
    }
    if (vehicleData.status) {
      formData.append('status', vehicleData.status);
    }
    if (vehicleData.department) {
      formData.append('department', vehicleData.department);
    }
    
    // Add image file if provided
    if (imageFile) {
      formData.append('image_file', imageFile);
    }
    
    return apiClient.post('/vehicles/', formData, {
      headers: {
        'Content-Type': undefined, // Let the browser set the Content-Type with boundary
      },
    });
  },

  async updateVehicle(vehicleId: number, updateData: FormData): Promise<Vehicle> {
    return apiClient.put(`/vehicles/${vehicleId}`, updateData, {
      headers: {
        'Content-Type': undefined, // Let the browser set the Content-Type with boundary
      },
    });
  },

  async getVehicleRequests(): Promise<any[]> {
    return apiClient.get('/vehicles/requests/');
  },

  async approveVehicleRequest(requestId: number, approvalData: any): Promise<any> {
    return apiClient.post(`/vehicles/requests/${requestId}/approve`, approvalData);
  },

  async inspectAndApproveVehicle(requestId: number, inspectionData: any): Promise<any> {
    return apiClient.post(`/vehicles/requests/${requestId}/inspect`, inspectionData);
  },

  async returnVehicle(vehicleId: number, returnData: any): Promise<any> {
    return apiClient.post(`/vehicles/return-vehicle`, {
      vehicle_id: vehicleId,
      ...returnData
    });
  },

  async returnVehicleDirect(vehicleId: number, returnData: any): Promise<any> {
    // First, we need to find the active request for this vehicle
    const requests = await this.getVehicleRequests();
    const activeRequest = requests.find(req => 
      req.vehicle_id === vehicleId && 
      req.status === 'Approved'
    );
    
    if (!activeRequest) {
      throw new Error('No active request found for this vehicle');
    }
    
    return apiClient.post(`/vehicles/requests/${activeRequest.id}/return`, returnData);
  },

  // Maintenance methods
  async getVehicleMaintenance(): Promise<any[]> {
    return apiClient.get('/vehicles/maintenance/');
  },

  async createMaintenance(maintenanceData: any): Promise<any> {
    return apiClient.post('/vehicles/maintenance/', maintenanceData);
  },

  async updateMaintenance(maintenanceId: number, updateData: any): Promise<any> {
    return apiClient.put(`/vehicles/maintenance/${maintenanceId}`, updateData);
  },

  async deleteMaintenance(maintenanceId: number): Promise<any> {
    return apiClient.delete(`/vehicles/maintenance/${maintenanceId}`);
  },
};
